/* eslint-disable max-statements */
import { Loader, MultiSelect as MantineMultiSelect, MultiSelectProps as MantineMultiSelectProps } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import React, { useEffect, useMemo, useState } from 'react';

import { MULTISELECT_DEBOUNCE_TIME } from '@constants';

export type MultiSelectItemType = string | { value?: string; label?: string; [key: string]: any };

export interface MultiSelectProps extends Omit<MantineMultiSelectProps, 'data'> {
  fetchOptions?: (query: string) => Promise<MultiSelectItemType[]>;
  data?: MultiSelectItemType[];
  debounceTime?: number;
  minChars?: number;
  initialOptions?: MultiSelectItemType[];
  errorMessage?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  fetchOptions,
  data = [],
  debounceTime = MULTISELECT_DEBOUNCE_TIME,
  minChars = 1,
  initialOptions = [],
  errorMessage = 'Failed to load options',
  onChange,
  onFocus,
  onBlur,
  value = [],
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<MultiSelectItemType[]>(fetchOptions ? initialOptions : data);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const floating = useMemo(() => {
    return value.length || query.length !== 0 || focused ? true : undefined;
  }, [query, focused, value]);

  const getItemLabel = (item: MultiSelectItemType): string => {
    if (typeof item === 'string') {
      return item;
    }
    return item.label;
  };

  const getItemValue = (item: MultiSelectItemType): string => {
    if (typeof item === 'string') {
      return item;
    }

    return item.value ?? item.label;
  };

  const filterItems = (items: MultiSelectItemType[], searchQuery: string): MultiSelectItemType[] => {
    const lowercaseQuery = searchQuery.toLowerCase();
    return items.filter((item) => {
      const label = getItemLabel(item);
      return label.toLowerCase().includes(lowercaseQuery);
    });
  };

  useEffect(() => {
    if (!fetchOptions) {
      setOptions(data);
    }
  }, [data, fetchOptions]);

  const handleChange = (values: string[]) => {
    setQuery(values[values.length - 1] || '');
    setOptions(filterItems(options, query));
    onChange(values);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    setFocused(true);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);
    setFocused(false);
  };

  const debouncedQuery = useDebouncedCallback(async (query: string) => {
    if (!fetchOptions && query.length <= minChars) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchOptions('');
      setOptions(result.filter((item) => !value.includes(getItemValue(item))));
    } catch (err) {
      setError(errorMessage);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, debounceTime);

  useEffect(() => {
    debouncedQuery(query);
  }, [query]);

  const processedData = (options || []).map((item) => {
    if (typeof item === 'string') {
      return item;
    }

    return {
      value: getItemValue(item),
      label: getItemLabel(item),
    };
  });

  return (
    <MantineMultiSelect
      {...props}
      value={value}
      checkIconPosition="left"
      data={processedData}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      rightSection={loading ? <Loader size="xs" /> : props.rightSection}
      error={error}
      data-floating={floating}
      labelProps={{ ...props.labelProps, 'data-floating': floating, 'data-error': props.error }}
      styles={{ pillsList: { paddingTop: '10px' } }}
    />
  );
};
